import { useCallback, useEffect, useRef, useState } from 'react';
import { useEvent } from '@soap/modules';
import { parseUnitCreationProgressLabel } from '../utils/unitCreationProgress';
import {
    labelToStageIndex,
    UNIT_CREATION_FINISHING_STEP_MS,
    UNIT_CREATION_PIPELINE_STAGE_LABELS,
    UNIT_CREATION_STAGE_LABELS
} from '../utils/unitCreationStages';

export type UnitCreationProgressState = {
    stageIndex: number;
    stages: readonly string[];
    /** Advance to the UI-only finishing step and resolve after it has been shown. */
    runFinishingStep: () => Promise<void>;
};

/**
 * Monotonic create-unit progress driven by live E335v1_UnitCreationProgress events
 * (published mid-handler via LiveWebSocketEventPublisher).
 */
export function useUnitCreationProgress(active: boolean): UnitCreationProgressState | null {
    const [stageIndex, setStageIndex] = useState(0);
    const stageIndexRef = useRef(0);
    const activeRef = useRef(active);
    const finishingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const finishingResolveRef = useRef<(() => void) | null>(null);
    activeRef.current = active;

    const clearFinishingTimer = useCallback(() => {
        if (finishingTimerRef.current != null) {
            clearTimeout(finishingTimerRef.current);
            finishingTimerRef.current = null;
        }
        if (finishingResolveRef.current) {
            const resolve = finishingResolveRef.current;
            finishingResolveRef.current = null;
            resolve();
        }
    }, []);

    const applyStageIndex = useCallback((nextIndex: number) => {
        // Server events only advance through real pipeline stages (not the UI-only finisher).
        const max = UNIT_CREATION_PIPELINE_STAGE_LABELS.length - 1;
        const clamped = Math.min(Math.max(0, nextIndex), max);
        if (clamped <= stageIndexRef.current) return;
        stageIndexRef.current = clamped;
        setStageIndex(clamped);
    }, []);

    const applyServerLabel = useCallback(
        (label: string) => {
            const idx = labelToStageIndex(label);
            if (idx >= 0) applyStageIndex(idx);
        },
        [applyStageIndex]
    );

    const runFinishingStep = useCallback(() => {
        return new Promise<void>(resolve => {
            const finishingIndex = UNIT_CREATION_STAGE_LABELS.length - 1;
            if (stageIndexRef.current < finishingIndex) {
                stageIndexRef.current = finishingIndex;
                setStageIndex(finishingIndex);
            }
            clearFinishingTimer();
            finishingResolveRef.current = resolve;
            finishingTimerRef.current = setTimeout(() => {
                finishingTimerRef.current = null;
                finishingResolveRef.current = null;
                resolve();
            }, UNIT_CREATION_FINISHING_STEP_MS);
        });
    }, [clearFinishingTimer]);

    useEffect(() => {
        if (!active) {
            clearFinishingTimer();
            stageIndexRef.current = 0;
            setStageIndex(0);
            return;
        }

        stageIndexRef.current = 0;
        setStageIndex(0);
    }, [active, clearFinishingTimer]);

    useEffect(() => () => clearFinishingTimer(), [clearFinishingTimer]);

    useEvent({
        eventName: 'E335v1_UnitCreationProgress',
        onEventReceived: (event: Record<string, unknown> | null | undefined) => {
            if (!activeRef.current) return;
            const label = parseUnitCreationProgressLabel(event);
            if (label) applyServerLabel(label);
        }
    });

    if (!active) return null;
    return {
        stageIndex,
        stages: UNIT_CREATION_STAGE_LABELS,
        runFinishingStep
    };
}
